import * as BoardConstants from "@/game/constants/boardConstants";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import type { GameState, PlayerColor } from "@nextgammon/shared";
import { Constants, getGameMode } from "@nextgammon/shared";
import type { BoardDimensions, CheckerMovement } from "./animationTypes";
import { ANIMATION_CONFIG } from "./animationTypes";
import { getCheckerPositions } from "./checkerDisplayUtils";

function getTargetMovementPosition(
  pointIndex: number,
  checkerCount: number,
  textureHeight: number,
  boardDimensions: BoardDimensions,
  isBearingOff: boolean
): { x: number; y: number } {
  let area: [number, number, number, number];
  let isTop: boolean;
  // Handle Bar and Bear-Off areas
  if (pointIndex < Constants.HOME_BOARD_WHITE_START) {
    area = isBearingOff ? boardDimensions.bearOffAreas["white"] : boardDimensions.barAreas["white"];
    checkerCount = 1;
    isTop = isBearingOff ? boardDimensions.boardIsFlipped : !boardDimensions.boardIsFlipped;
  } else if (pointIndex > Constants.HOME_BOARD_BLACK_END) {
    area = isBearingOff ? boardDimensions.bearOffAreas["black"] : boardDimensions.barAreas["black"];
    checkerCount = 1;
    isTop = isBearingOff ? !boardDimensions.boardIsFlipped : boardDimensions.boardIsFlipped;
  } else {
    // For regular points, use the point area
    area = boardDimensions.pointAreas[pointIndex];
    isTop = !boardDimensions.boardIsFlipped ? pointIndex >= 12 : pointIndex < 12; // Determine if it's a top point
  }
  // For regular points, use getCheckerPositions to get the topmost position
  const positions = getCheckerPositions(area, checkerCount, textureHeight, isTop);
  return positions[positions.length - 1]; // Get the topmost checker
}

export function detectCheckerMovements(
  prevState: GameState | null,
  currentState: GameState,
  boardDimensions: BoardDimensions,
  isReverse: boolean
): CheckerMovement[] {
  if (!prevState) return [];

  const movements: CheckerMovement[] = [];
  const timestamp = Date.now();
  const boundsScale = boardDimensions.boardScale * BoardConstants.POINT_CHECKER_SCALE;
  const textureHeight = boardDimensions.unscaledCheckerHeight * boundsScale;

  for (let pointIndex = -1; pointIndex <= 24; pointIndex++) {
    const prevPoint = getPointState(prevState, pointIndex, false, isReverse);
    const currentPoint = getPointState(currentState, pointIndex, false, isReverse);

    GameLogger.logDebug(
      "movementDetection",
      `Point ${pointIndex}: prev=${JSON.stringify(prevPoint)}, current=${JSON.stringify(currentPoint)}`
    );

    if (prevPoint.count !== currentPoint.count || prevPoint.player !== currentPoint.player) {
      if (prevPoint.player) {
        let checkersRemoved = prevPoint.count - currentPoint.count;
        // Ownership changes can happen with standard points but not with bar or bear-off areas
        if (
          pointIndex >= Constants.HOME_BOARD_WHITE_START &&
          pointIndex <= Constants.HOME_BOARD_BLACK_END
        ) {
          // Handling for bumps
          if (checkersRemoved === 0) {
            checkersRemoved = 1;
          }
        }

        for (let i = 0; i < checkersRemoved; i++) {
          const fromPosition = getTargetMovementPosition(
            pointIndex,
            prevPoint.count,
            textureHeight,
            boardDimensions,
            prevPoint.isBearingOff
          );
          // For ID and potential future use
          const checkerId = `${prevPoint.player}-${pointIndex}-${timestamp}`;

          GameLogger.logDebug(
            "detectCheckerMovements",
            `Movement added for player ${prevPoint.player} from point ${pointIndex}`
          );

          movements.push({
            checkerId,
            fromPoint: pointIndex,
            toPoint: -999, // Placeholder, will be updated
            fromPosition,
            toPosition: { x: -1000, y: -1000 }, // Placeholder
            playerColor: prevPoint.player,
            fromIsBar:
              (Constants.HOME_BOARD_WHITE_START > pointIndex ||
                pointIndex > Constants.HOME_BOARD_BLACK_END) &&
              !prevPoint.isBearingOff,
            toIsBar: false,
            isReverse
          });
        }
      }
    }
  }

  GameLogger.logDebug("detectCheckerMovements", `Initial movements detected: ${movements.length}`);

  for (let pointIndex = -1; pointIndex <= 24; pointIndex++) {
    const prevPoint = getPointState(prevState, pointIndex, true, isReverse);
    const currentPoint = getPointState(currentState, pointIndex, true, isReverse);

    if (prevPoint.count !== currentPoint.count || prevPoint.player !== currentPoint.player) {
      if (currentPoint.count >= prevPoint.count && currentPoint.player) {
        const checkersAdded = Math.max(1, currentPoint.count - prevPoint.count);
        for (let i = 0; i < checkersAdded; i++) {
          // Calculate the stack offset for the checker
          const stackOffset = currentPoint.count - (checkersAdded - 1 - i);
          const toPosition = getTargetMovementPosition(
            pointIndex,
            stackOffset,
            textureHeight,
            boardDimensions,
            currentPoint.isBearingOff
          );

          const matchingMovement = movements.find(
            (m) => m.playerColor === currentPoint.player && m.toPoint === -999
          );

          if (matchingMovement) {
            GameLogger.logDebug(
              "detectCheckerMovements",
              `Updating movement for player ${currentPoint.player} to point ${pointIndex} from point ${matchingMovement.fromPoint}`
            );

            matchingMovement.toPoint = pointIndex;
            matchingMovement.toPosition = toPosition;
            matchingMovement.toIsBar =
              (Constants.HOME_BOARD_WHITE_START > pointIndex ||
                pointIndex > Constants.HOME_BOARD_BLACK_END) &&
              !currentPoint.isBearingOff;
          } else {
            GameLogger.logInfo(
              "detectCheckerMovements",
              `No matching movement found for player ${currentPoint.player} at point ${pointIndex}`
            );
          }
        }
      }
    }
  }

  const filteredMovements = movements.filter((m) => m.toPoint !== -999);
  // Ensure that movements to the bar happen last (better for visualizing a bump)
  filteredMovements.sort((a, b) => {
    if (a.toIsBar === b.toIsBar) return 0;
    return a.toIsBar ? 1 : -1;
  });
  return filteredMovements;
}

export function calculateDuration(
  fromPos: { x: number; y: number },
  toPos: { x: number; y: number },
  gameMode: string, // NEW parameter
  isPlayerTurn?: boolean,
  isReverse?: boolean
): number {
  const mode = getGameMode(gameMode);
  const { animation } = mode;

  const distance = Math.sqrt(Math.pow(toPos.x - fromPos.x, 2) + Math.pow(toPos.y - fromPos.y, 2));
  let duration = (distance / animation.checkerBaseSpeed) * 1000;

  // Keep existing multipliers
  if (!isPlayerTurn) {
    duration *= ANIMATION_CONFIG.OPPONENT_DURATION_MULTIPLIER; // Was 1.2
  }
  if (isReverse) {
    duration *= ANIMATION_CONFIG.REVERSE_DURATION_MULTIPLIER; // Was 0.7
  }

  return Math.max(animation.checkerMinDuration, Math.min(animation.checkerMaxDuration, duration));
}

function getPointState(
  state: GameState,
  pointIndex: number,
  moveTo: boolean,
  isReverse: boolean
): { count: number; player: PlayerColor | null; isBearingOff: boolean } {
  if (pointIndex < Constants.HOME_BOARD_WHITE_START) {
    if (!state.currentPlayerColor) {
      return { count: 0, player: null, isBearingOff: false };
    }
    // From the start of the movement
    if (!moveTo) {
      if (!isReverse) {
        return { count: state.bar.white, player: Constants.PLAYER_WHITE, isBearingOff: false };
      } else {
        if (state.currentPlayerColor === Constants.PLAYER_WHITE) {
          return { count: state.off.white, player: Constants.PLAYER_WHITE, isBearingOff: true };
        } else {
          return { count: state.bar.white, player: Constants.PLAYER_WHITE, isBearingOff: false };
        }
      }
    } else {
      // To the end of the movement
      if (!isReverse) {
        if (state.currentPlayerColor === Constants.PLAYER_WHITE) {
          return { count: state.off.white, player: Constants.PLAYER_WHITE, isBearingOff: true };
        } else {
          return { count: state.bar.white, player: Constants.PLAYER_WHITE, isBearingOff: false };
        }
      } else {
        return { count: state.bar.white, player: Constants.PLAYER_WHITE, isBearingOff: false };
      }
    }
  }
  if (pointIndex > Constants.HOME_BOARD_BLACK_END) {
    if (!state.currentPlayerColor) {
      return { count: 0, player: null, isBearingOff: false };
    }
    // From the start of the movement
    if (!moveTo) {
      if (!isReverse) {
        return { count: state.bar.black, player: Constants.PLAYER_BLACK, isBearingOff: false };
      } else {
        if (state.currentPlayerColor === Constants.PLAYER_BLACK) {
          return { count: state.off.black, player: Constants.PLAYER_BLACK, isBearingOff: true };
        } else {
          return { count: state.bar.black, player: Constants.PLAYER_BLACK, isBearingOff: false };
        }
      }
    } else {
      // To the end of the movement
      if (!isReverse) {
        if (state.currentPlayerColor === Constants.PLAYER_BLACK) {
          return { count: state.off.black, player: Constants.PLAYER_BLACK, isBearingOff: true };
        } else {
          return { count: state.bar.black, player: Constants.PLAYER_BLACK, isBearingOff: false };
        }
      } else {
        return { count: state.bar.black, player: Constants.PLAYER_BLACK, isBearingOff: false };
      }
    }
  }

  const point = state.board[pointIndex];
  return point
    ? { count: point.count, player: point.player, isBearingOff: false }
    : { count: 0, player: null, isBearingOff: false };
}
